import { useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { useCounties, type CountyEntry } from "../../hooks/useCounties";

interface Props {
  onSelect: (entry: CountyEntry) => void;
}

const MAX_RESULTS = 8;

export function CountySearch({ onSelect }: Props) {
  const counties = useCounties();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return counties
      .filter((c) => c.name.toLowerCase().includes(q))
      .slice(0, MAX_RESULTS);
  }, [counties, query]);

  const commit = (entry: CountyEntry) => {
    onSelect(entry);
    setQuery("");
    setOpen(false);
    setHighlight(0);
    inputRef.current?.blur();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open || matches.length === 0) {
      if (e.key === "Escape") inputRef.current?.blur();
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, matches.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        commit(matches[highlight]);
        break;
      case "Escape":
        setOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  return (
    <div
      style={{
        position: "absolute",
        top: 12,
        // Zoom control was moved to topright so we can hug the left edge.
        left: 12,
        zIndex: 700,
        width: 240,
        fontFamily: "inherit",
      }}
    >
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // delay so a mouse click on a row can fire first
          setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={onKeyDown}
        placeholder="Search county…"
        aria-label="Search county"
        style={{
          width: "100%",
          padding: "6px 10px",
          fontSize: 13,
          color: "#111827",
          background: "#ffffff",
          border: "1px solid #d1d5db",
          borderRadius: 6,
          boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
          outline: "none",
        }}
      />
      {open && matches.length > 0 && (
        <ul
          role="listbox"
          style={{
            listStyle: "none",
            margin: "4px 0 0",
            padding: 4,
            background: "#ffffff",
            border: "1px solid #d1d5db",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            maxHeight: 280,
            overflowY: "auto",
          }}
        >
          {matches.map((c, i) => {
            const isActive = i === highlight;
            return (
              <li
                key={c.geoid}
                role="option"
                aria-selected={isActive}
                onMouseDown={(e) => {
                  // mousedown so we beat the input's onBlur timeout
                  e.preventDefault();
                  commit(c);
                }}
                onMouseEnter={() => setHighlight(i)}
                style={{
                  padding: "6px 8px",
                  fontSize: 13,
                  color: isActive ? "#111827" : "#374151",
                  background: isActive ? "#e5e7eb" : "transparent",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                {c.name}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
